/**
 * AppShell — Main layout component replacing PageLayout.
 * Desktop: persistent sidebar (240px) + topbar + content area.
 * Mobile: hidden sidebar with hamburger, topbar + content area.
 */
import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';

import SidebarNav from './SidebarNav';
import SecondaryNav from './SecondaryNav';
import NotificationBell from './NotificationBell';
import useNotifications from '../hooks/useNotifications';
import { useUserRole } from '../contexts/UserRoleContext';
import { getPageTitle, getUserMenuItems, isFullScreenPath, hidesSecondaryNav, isHubPath } from '../config/navigation';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const stringToColor = () => '#556071'; // Fixed slate — no random colors

const TOPBAR_HEIGHT = 44;

export default function AppShell({ onSearchClick, children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { instance } = useMsal();
  const { permissions } = useUserRole();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const activeAccount = instance.getActiveAccount();
  const userName = activeAccount?.name || 'Gebruiker';
  const userEmail = activeAccount?.username || '';
  const { notifications, dismiss, dismissAll } = useNotifications(userEmail, userName);
  const userMenuItems = getUserMenuItems(permissions);

  const pageTitle = getPageTitle(location.pathname);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    handleMenuClose();
    instance.logoutRedirect();
  };

  // Layout-metadata komt centraal uit config/navigation.js — géén eigen lijsten hier.
  // Full-screen editors verbergen zowel de hoofd-sidebar als de secundaire nav;
  // callsheets houden de hoofd-sidebar maar verliezen de secundaire sub-nav.
  const isFullWidthPage = isFullScreenPath(location.pathname);
  const hideSecondaryNav = hidesSecondaryNav(location.pathname);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar — persistent on desktop (hidden for full-width pages), drawer on mobile */}
      <SidebarNav
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        hidePersistent={isFullWidthPage}
        onSearchClick={onSearchClick}
      />

      {/* Main area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          minHeight: '100vh',
        }}
      >
        {/* Topbar */}
        <AppBar
          position="sticky"
          elevation={0}
          color="inherit"
          sx={{
            background: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderBottom: 'none',
            boxShadow: '0 10px 30px rgba(45,51,53,0.04)',
            zIndex: theme.zIndex.appBar,
          }}
        >
          <Toolbar
            sx={{
              minHeight: `${TOPBAR_HEIGHT}px !important`,
              maxHeight: `${TOPBAR_HEIGHT}px !important`,
              px: { xs: 1.5, sm: 2 },
              gap: 0.5,
            }}
          >
            {/* Left: hamburger (mobile) + page title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
              {(isMobile || isFullWidthPage) && (
                <IconButton
                  onClick={() => setMobileOpen(true)}
                  size="small"
                  sx={{ color: 'text.disabled' }}
                >
                  <MenuIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
              {/* Hide title on hub pages — body header is leidend */}
              {!isHubPath(location.pathname) && (
                <Typography
                  noWrap
                  sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'text.secondary', letterSpacing: '-0.01em' }}
                >
                  {pageTitle}
                </Typography>
              )}
            </Box>

            {/* Right: search + notifications + profile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Tooltip title="Zoeken (Ctrl+K)">
                <IconButton onClick={onSearchClick} size="small" sx={{ color: 'text.disabled' }}>
                  <SearchIcon sx={{ fontSize: 19 }} />
                </IconButton>
              </Tooltip>

              {/* Notificatiecentrum tijdelijk verborgen — komt later terug */}
              {false && (
                <NotificationBell
                  notifications={notifications}
                  onDismiss={dismiss}
                  onDismissAll={dismissAll}
                />
              )}

              <Tooltip title={userName}>
                <IconButton onClick={handleMenuOpen} size="small" sx={{ ml: 0.25 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      bgcolor: '#635983',
                      border: '2px solid rgba(99,89,131,0.2)',
                    }}
                  >
                    {getInitials(userName)}
                  </Avatar>
                </IconButton>
              </Tooltip>

              {/* Profile menu */}
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 200,
                    borderRadius: 2,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                    {userName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                    {userEmail}
                  </Typography>
                </Box>

                {userMenuItems.map((item) => (
                  <MenuItem
                    key={item.path}
                    component={RouterLink}
                    to={item.path}
                    onClick={handleMenuClose}
                    sx={{ py: 1, fontSize: '0.82rem' }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <item.Icon sx={{ fontSize: 17, color: 'text.secondary' }} />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: '0.82rem' }}>{item.label}</ListItemText>
                  </MenuItem>
                ))}

                <Divider />

                <MenuItem onClick={handleLogout} sx={{ py: 1, fontSize: '0.82rem', color: 'text.secondary' }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <LogoutIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: '0.82rem' }}>Uitloggen</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            minHeight: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
            minWidth: 0,
          }}
        >
          {!hideSecondaryNav && <SecondaryNav />}
          <Box
            component="main"
            sx={{
              flex: 1,
              overflow: 'auto',
              minWidth: 0,
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
